import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import type { ThemeOption } from './types'

declare global {
  interface Window {
    __wadTheme?: { option: ThemeOption; resolved: 'dark' | 'light' }
  }
}

function getCookieValue(name: string): string | null {
  return document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))?.[1] ?? null
}

function getRootDomain(): string {
  const parts = window.location.hostname.split('.')
  return parts.length > 1 ? '.' + parts.slice(-2).join('.') : window.location.hostname
}

export function useMenuTheme(opts: {
  themeProp: () => ThemeOption | undefined
  onThemeChange: (option: ThemeOption) => void
}) {
  const internalTheme = ref<ThemeOption>('system')

  function applyTheme(option: ThemeOption) {
    if (typeof document === 'undefined') return
    const isDark = option === 'dark' ||
      (option === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
    const resolved: 'dark' | 'light' = isDark ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme', resolved)

    // Expose for external pages
    window.__wadTheme = { option, resolved }
    window.dispatchEvent(new CustomEvent('wad-theme-change', {
      detail: { option, resolved }
    }))
  }

  let mediaQuery: MediaQueryList | undefined
  function onSystemThemeChange() {
    if (internalTheme.value === 'system') applyTheme('system')
  }

  onMounted(() => {
    if (opts.themeProp() !== undefined) {
      applyTheme(opts.themeProp()!)
      return // consumer manages theme
    }

    // Clean up localStorage keys that are no longer used (one-time migration)
    if (localStorage.getItem('themeOption') !== null) localStorage.removeItem('themeOption')
    if (localStorage.getItem('wad-theme') !== null) localStorage.removeItem('wad-theme')

    // Cookie is the only shared state across subdomains — localStorage is per-origin and cannot be used
    const stored = (getCookieValue('themeOption') ?? 'system') as ThemeOption
    internalTheme.value = stored
    applyTheme(stored)

    mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)')
    mediaQuery?.addEventListener('change', onSystemThemeChange)
  })

  onBeforeUnmount(() => {
    mediaQuery?.removeEventListener('change', onSystemThemeChange)
  })

  watch(() => opts.themeProp(), (val) => {
    if (val !== undefined) applyTheme(val)
  })

  const resolvedTheme = computed<ThemeOption>(() => opts.themeProp() ?? internalTheme.value)

  function setTheme(option: ThemeOption) {
    opts.onThemeChange(option)
    if (opts.themeProp() !== undefined) {
      return
    }

    internalTheme.value = option
    const rootDomain = getRootDomain()
    const isDark = option === 'dark' || (option === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    document.cookie = `themeOption=${option};path=/;domain=${rootDomain};max-age=31536000`
    document.cookie = `theme=${isDark ? 'dark' : 'light'};path=/;domain=${rootDomain};max-age=31536000`
    applyTheme(option)
  }

  return { resolvedTheme, setTheme }
}
